import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { isLiberiaApiEnabled, touchLiberiaSessionActivity } from '@/utils/liberiaApi';

/** Minimum time between activity pings so idle logout is not kept alive by a timer. */
const ACTIVITY_HEARTBEAT_MS = 60 * 1000;

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'] as const;

/**
 * Updates `user_sessions.last_activity` only when the user is actually interacting.
 * A fixed interval would reset last_activity and block the 15-minute idle logout.
 */
export const useSessionTracking = () => {
  const { user, forceSessionCheck } = useAuth();
  const lastPingRef = useRef(0);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!user || !isLiberiaApiEnabled()) {
      return;
    }

    const ping = async () => {
      const now = Date.now();
      if (inFlightRef.current || now - lastPingRef.current < ACTIVITY_HEARTBEAT_MS) {
        return;
      }

      inFlightRef.current = true;
      lastPingRef.current = now;

      try {
        await touchLiberiaSessionActivity({
          deviceInfo: {
            source: 'web',
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          },
        });
      } catch {
        // Session cookie may be missing or the server already expired an idle session.
        try {
          await forceSessionCheck();
        } catch {
          // ignore
        }
      } finally {
        inFlightRef.current = false;
      }
    };

    void ping();

    const onActivity = () => {
      void ping();
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity);
      });
    };
  }, [user, forceSessionCheck]);

  return {};
};
